import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {ItemParams} from 'react-contexify';
import {contextMenu} from 'react-contexify';
import isEqual from 'react-fast-compare';

import {reaction} from 'mobx';

import type {FileApiModel} from '@yourcoach/shared/api/media/file';
import type {LinkApiModel as Link} from '@yourcoach/shared/api/media/link';

import {i18n} from '@yourcoach/i18n';
import {editionStore} from '@yourcoach/shared/api/program';
import DocumentIcon from '@yourcoach/shared/assets/icons/primary/Paper.svg';
import {alert} from '@yourcoach/shared/components/alert';
import {
  EVERGREEN_PROGRAM_SCHEDULE_DURATION,
  isEditionEvergreen,
} from '@yourcoach/shared/utils/entity/program';
import {logger} from '@yourcoach/shared/utils/logger';

import type {ScheduleMaterial} from '@src/modules/materials/utils';

import Button from '@src/components/Button';
import Loader from '@src/components/Loader/Loader';
import ModalAnimateWin from '@src/components/ModalAnimateWin/ModalAnimateWin';
import NoResultsHeader from '@src/components/NoResultsHeader';
import useIsVisible from '@src/hooks/useIsVisible';
import {linksToMaterials, materialIsFolder} from '@src/modules/materials/utils';
import LibraryState from '@src/modules/MyLibrary/context/LibraryState';
import AddFile from '@src/pages/CRUProgram/legacy/AddFile';
import {ContextMenu} from '@src/v2/components/ContextMenu';

import type {ProgramT} from '.';
import type {TabProps, TabRef} from './useTabs';

import LibraryListItem from './LibraryListItem';
import MaterialPublicationDayModal from './MaterialPublicationDayModal';

import styles from './Library.module.css';

const MATERIAL_CONTEXT_MENU_ID = 'material_context_menu';

const CRUProgramLibrary = forwardRef<TabRef, TabProps>(({program}, ref) => {
  useImperativeHandle(ref, () => ({
    getData,
  }));

  const [materials, setMaterials] = useState<ScheduleMaterial[]>([]);

  const isMaterialInDurationRange = useCallback(
    (material: ScheduleMaterial) => {
      if (program) {
        const lastEdition = program.editions.slice().pop();

        if (lastEdition) {
          const programDuration = isEditionEvergreen(lastEdition)
            ? EVERGREEN_PROGRAM_SCHEDULE_DURATION
            : lastEdition.duration;
          const maxDay = programDuration - 1;

          if (material.day > maxDay) {
            return false;
          }
        }
      }

      return true;
    },
    [program],
  );

  const shouldRenderStub = useMemo(
    () => !materials.filter(isMaterialInDurationRange).length,
    [isMaterialInDurationRange, materials],
  );

  const listData = useMemo(() => {
    let result: ScheduleMaterial[] = [];

    if (materials.length) {
      const folders: ScheduleMaterial[] = [];
      const files: ScheduleMaterial[] = [];

      materials.forEach((material: ScheduleMaterial) => {
        if (materialIsFolder(material)) {
          folders.push(material);
        } else {
          files.push(material);
        }
      });

      const sortFn = (a: ScheduleMaterial, b: ScheduleMaterial) =>
        a.day - b.day;

      result = folders
        .concat(files)
        .filter(isMaterialInDurationRange)
        .sort(sortFn);
    }

    return result;
  }, [materials, isMaterialInDurationRange]);

  const [addMaterialModalIsOpen, showAddMaterialModal, hideAddMaterialModal] =
    useIsVisible();

  const [overlayIsVisible, showOverlay, hideOverlay] = useIsVisible();

  const [unlockDateModalIsOpen, showUnlockDateModal, hideUnlockDateModal] =
    useIsVisible();

  const materialToEdit = useRef<ScheduleMaterial>(undefined);

  const getData = useCallback(() => {
    let lastEdition: null | ProgramT['editions'][0] =
      program && program.editions.length
        ? program.editions[program.editions.length - 1]!
        : null;

    lastEdition = {
      ...lastEdition,
      materials: materials.slice().filter(isMaterialInDurationRange),
    } as ProgramT['editions'][0];

    let editions: ProgramT['editions'];

    if (program && program.editions.length) {
      program.editions.splice(program.editions.length - 1, 1, lastEdition);

      editions = program.editions;
    } else {
      editions = [lastEdition];
    }

    return {
      editions,
    };
  }, [isMaterialInDurationRange, materials, program]);

  useEffect(() => {
    if (program) {
      const lastEdition = program.editions[program.editions.length - 1];

      if (lastEdition) {
        setMaterials(lastEdition.materials || materials);
      }
    }

    const dispose = reaction(
      () => editionStore.updating,
      updating => {
        if (
          program &&
          updating.success &&
          updating.entity &&
          updating.entity._id === program._id
        ) {
          const lastEdition = program.editions[program.editions.length - 1];

          if (lastEdition) {
            setMaterials(lastEdition.materials || materials);
          }
        }
      },
    );

    return () => {
      dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddTaskButtonClick = useCallback(() => {
    showAddMaterialModal();
  }, [showAddMaterialModal]);

  const createMaterials = useCallback(
    async (links: (Link & {resource: FileApiModel})[]) => {
      try {
        showOverlay();

        const createdMaterials = await Promise.all(linksToMaterials(links));

        const emptyFolders: Link[] = [];
        const failedLinks: Link[] = [];
        const materialsToAdd: ScheduleMaterial[] = [];

        createdMaterials.forEach((item, i) => {
          // @ts-expect-error: internal fields
          if (!item.emptyFolder && !item.isError) {
            materialsToAdd.push(item as ScheduleMaterial);
            // @ts-expect-error: internal fields
          } else if (item.emptyFolder) {
            emptyFolders.push(links[i]!);
          }
        });

        if (materialsToAdd.length) {
          setMaterials(prev => [...prev, ...materialsToAdd]);
        }

        if (failedLinks.length || emptyFolders.length) {
          let message = '';

          if (emptyFolders.length) {
            message = i18n.t('shared:CRUProgramLibrary.empty_folders_error', {
              count: emptyFolders.length,
              names: emptyFolders.map(item => item.name).join(', '),
            });
          }

          if (failedLinks.length) {
            message = message
              ? `${message}${message[message.length - 1] === '.' ? ' ' : '. '}`
              : '';

            message = `${message}${i18n.t(
              'shared:CRUProgramLibrary.failed_links_error',
              {
                count: failedLinks.length,
                userNames: failedLinks.map(item => item.name).join(', '),
              },
            )}`;
          }

          alert.error(new Error(message));
        }

        hideOverlay();
      } catch (error) {
        hideOverlay();

        logger.error(error);

        alert.error(error);
      }
    },
    [hideOverlay, showOverlay],
  );

  const onChooseMaterials = useCallback(
    chosenMaterials => {
      createMaterials(chosenMaterials);

      hideAddMaterialModal();
    },
    [createMaterials, hideAddMaterialModal],
  );

  const onClearButtonClick = useCallback(async () => {
    const isConfirmed = await alert.confirm();

    if (!isConfirmed) {
      return;
    }

    setMaterials([]);
  }, []);

  const onMaterialMoreButtonClick = useCallback(
    (
      material: ScheduleMaterial,
      event: React.MouseEvent<Element, MouseEvent>,
    ) => {
      contextMenu.show({
        event,
        id: MATERIAL_CONTEXT_MENU_ID,
        props: {
          material,
        },
      });
    },
    [],
  );

  const onChangeUnlockDateButtonClick = useCallback(
    ({props}: ItemParams<{material: ScheduleMaterial}>) => {
      if (props) {
        const {material} = props;

        materialToEdit.current = material;

        showUnlockDateModal();
      }
    },
    [showUnlockDateModal],
  );

  const onMaterialPublicationDayButtonClick = useCallback(
    (material: ScheduleMaterial) => {
      contextMenu.hideAll();

      materialToEdit.current = material;

      showUnlockDateModal();
    },
    [showUnlockDateModal],
  );

  const onDeleteButtonClick = useCallback(
    ({props}: ItemParams<{material: ScheduleMaterial}>) => {
      if (props) {
        const {material} = props;

        setMaterials(prev => {
          const index = prev.findIndex(item => material.uuid === item.uuid);

          if (index >= 0) {
            prev.splice(index, 1);
          }

          return [...prev];
        });
      }
    },
    [],
  );

  const onMaterialUnlockDateChange = useCallback(
    (material: ScheduleMaterial) => {
      hideUnlockDateModal();

      setMaterials(prev => {
        const index = prev.findIndex(item => material.uuid === item.uuid);

        if (index >= 0) {
          prev.splice(index, 1, material);
        }

        return [...prev];
      });
    },
    [hideUnlockDateModal],
  );

  const renderContextMenu = useCallback(
    () => (
      <ContextMenu.Menu animation="fade" id={MATERIAL_CONTEXT_MENU_ID}>
        <ContextMenu.Item onClick={onChangeUnlockDateButtonClick}>
          {i18n.t('shared:change_unlock_date')}
        </ContextMenu.Item>
        <ContextMenu.Item onClick={onDeleteButtonClick} variant="error">
          {i18n.t('shared:remove')}
        </ContextMenu.Item>
      </ContextMenu.Menu>
    ),
    [onChangeUnlockDateButtonClick, onDeleteButtonClick],
  );

  const onUnlockDateModalClose = useCallback(() => {
    materialToEdit.current = undefined;
    hideUnlockDateModal();
  }, [hideUnlockDateModal]);

  return (
    <>
      <div className={styles.addButtonsContainer}>
        <Button onClick={onAddTaskButtonClick}>
          <DocumentIcon />
          {i18n.t('shared:CRUProgramLibrary.add_materials_button')}
        </Button>
        {!shouldRenderStub ? (
          <Button className={styles.clearButton} onClick={onClearButtonClick}>
            {i18n.t('shared:CRUProgramLibrary.clear_button')}
          </Button>
        ) : null}
      </div>
      {shouldRenderStub ? (
        <div className={styles.noResultsContainer}>
          <NoResultsHeader
            icon={DocumentIcon}
            text={i18n.t('shared:CRUProgramLibrary.no_results_label')}
          />
        </div>
      ) : (
        <div className={styles.list}>
          {listData.map(item => (
            <LibraryListItem
              key={item.uuid}
              material={item}
              onMoreButtonClick={onMaterialMoreButtonClick}
              onPublicationDayButtonClick={onMaterialPublicationDayButtonClick}
            />
          ))}
        </div>
      )}
      <ModalAnimateWin
        className="greyHeaderContainer"
        classNameBody="whiteBody"
        classNameCloseBut="greyHeaderBut"
        classNameHeader="greyHeader"
        closeModalHandler={hideAddMaterialModal}
        header={i18n.t('shared:CRUProgramLibrary.add_materials_button')}
        isBody
        showModal={addMaterialModalIsOpen}
      >
        <LibraryState>
          <AddFile getListItems={onChooseMaterials} />
        </LibraryState>
      </ModalAnimateWin>
      {program && materialToEdit.current ? (
        <MaterialPublicationDayModal
          isOpen={unlockDateModalIsOpen}
          material={materialToEdit.current}
          onAfterClose={onUnlockDateModalClose}
          onSuccess={onMaterialUnlockDateChange}
          program={program}
        />
      ) : null}
      {renderContextMenu()}
      {overlayIsVisible ? (
        <div className="overlay">
          <Loader />
        </div>
      ) : null}
    </>
  );
});

export default memo(CRUProgramLibrary, isEqual);
